import { jsPDF } from 'jspdf';
import { orgWallClock } from '@/lib/utils/custom-date/custom-date';
import type { OrganizationLetterhead } from '@/hooks/use-organizations';

/** The letter's letterhead: the organization's own, each empty part filled from `DEFAULT_LETTERHEAD`. */
export interface NocLetterhead {
  companyName: string;
  logoUrl: string;
  addressLines: string[];
  contactLines: [label: string, value: string][];
  website: string;
  email: string;
}

export interface NocLetterContent {
  /** Reference number without the date suffix, e.g. `SELISEBD/NOC/2026/09/03`. */
  documentNumber: string;
  letterDate: string;
  applicantName: string;
  body: string;
  /** Who approved (generated) the letter: its signature. */
  approverName: string;
  approverDesignation: string;
  /** The day it was approved, formatted. */
  approvedOn: string;
  /** The contact person HR entered, kept under the signature so the letter can be verified. */
  pocName: string;
  pocDesignation: string;
  pocEmail: string;
  pocMobile: string;
  letterhead: NocLetterhead;
}

export type NocCopy = 'hard' | 'soft';

export const NOC_ASSET_BASE = '/assets/noc';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 20;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

/** The legacy letterhead (SELISE Bangladesh), used for whatever the organization's IAM record leaves empty. */
export const DEFAULT_LETTERHEAD: NocLetterhead = {
  companyName: 'SELISE Bangladesh Ltd.',
  logoUrl: `${NOC_ASSET_BASE}/selisehrlogo.webp`,
  addressLines: [
    'Office Dhaka:',
    'Midas Center',
    'House 05, Road 16(new),',
    'Dhanmondi R/A',
    'Dhaka-1209',
    'Bangladesh',
  ],
  contactLines: [
    ['Phone', '+880-2-41021495'],
    ['Mobile', '+88 01711126465'],
    ['CH Line', '044 8058049'],
    ['US Line', '(213) 260-1291'],
  ],
  website: 'www.selisegroup.com',
  email: 'info@selisegroup.com',
};

/** The organization's letterhead (agreed 2 Nov 2026), part by part falling back to the legacy one. */
export const letterheadOf = (organization?: OrganizationLetterhead): NocLetterhead => ({
  companyName: organization?.legalName ?? DEFAULT_LETTERHEAD.companyName,
  logoUrl: organization?.logoUrl ?? DEFAULT_LETTERHEAD.logoUrl,
  addressLines: organization?.addressLines.length
    ? organization.addressLines
    : DEFAULT_LETTERHEAD.addressLines,
  contactLines: organization?.phone ? [['Phone', organization.phone]] : DEFAULT_LETTERHEAD.contactLines,
  website: organization?.website ?? DEFAULT_LETTERHEAD.website,
  email: organization?.email ?? DEFAULT_LETTERHEAD.email,
});

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
};

/** Draws the image on a canvas so jsPDF gets a PNG even for webp sources; null if it cannot load. */
const loadImageAsPng = (url: string) =>
  new Promise<string | null>((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d');
      if (!context) return resolve(null);
      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });

/** Registers the Ubuntu font the legacy letter used; falls back to a built-in font if it is missing. */
const registerFont = async (pdf: jsPDF) => {
  try {
    const [regular, bold] = await Promise.all([
      fetch(`${NOC_ASSET_BASE}/Ubuntu-Regular.ttf`).then((response) => response.arrayBuffer()),
      fetch(`${NOC_ASSET_BASE}/Ubuntu-Bold.ttf`).then((response) => response.arrayBuffer()),
    ]);
    pdf.addFileToVFS('Ubuntu-Regular.ttf', arrayBufferToBase64(regular));
    pdf.addFont('Ubuntu-Regular.ttf', 'Ubuntu', 'normal');
    pdf.addFileToVFS('Ubuntu-Bold.ttf', arrayBufferToBase64(bold));
    pdf.addFont('Ubuntu-Bold.ttf', 'Ubuntu', 'bold');
    return 'Ubuntu';
  } catch {
    return 'helvetica';
  }
};

/**
 * Downloads the NOC letter as a PDF. A *hard* copy is plain text meant to be printed on the
 * company letterhead; a *soft* copy carries the logo, the stamp and the digital-signature footer.
 */
export const downloadNocPdf = async (copy: NocCopy, letter: NocLetterContent) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const font = await registerFont(pdf);
  const branded = copy === 'soft';
  const { letterhead } = letter;

  const [logo, stamp] = branded
    ? await Promise.all([
        loadImageAsPng(letterhead.logoUrl),
        loadImageAsPng(`${NOC_ASSET_BASE}/selise_stamp.png`),
      ])
    : [null, null];

  if (logo) pdf.addImage(logo, 'PNG', MARGIN - 2.2, 20, 45, 20, undefined, 'FAST');

  pdf.setTextColor(0, 0, 0);
  pdf.setFont(font, 'normal');

  // Company address block, top right.
  const rightX = 140;
  let rightY = 44;
  pdf.setFontSize(8);
  [letterhead.companyName, ...letterhead.addressLines].forEach((line, index) => {
    pdf.text(line, rightX, rightY);
    rightY += index === 0 ? 4.5 : 3.5;
  });
  rightY += 2;
  letterhead.contactLines.forEach(([label, value]) => {
    pdf.text(label, rightX, rightY);
    pdf.text(value, rightX + 16, rightY);
    rightY += 3.5;
  });
  rightY += 2;
  pdf.text(letterhead.website, rightX, rightY);
  pdf.text(letterhead.email, rightX, rightY + 3.5);

  // Reference number and date, top left.
  pdf.setFontSize(11);
  pdf.text(letter.documentNumber || 'N/A', MARGIN, 52);
  pdf.text(letter.letterDate || 'N/A', MARGIN, 57);

  let y = 115;
  pdf.setFont(font, 'bold');
  pdf.setFontSize(14);
  pdf.text('TO WHOM IT MAY CONCERN', PAGE_WIDTH / 2, y, { align: 'center' });
  y += 15;

  pdf.setFont(font, 'normal');
  pdf.setFontSize(11);
  const paragraphs = letter.body.split('\n').filter((paragraph) => paragraph.trim().length > 0);
  paragraphs.forEach((paragraph, index) => {
    const lines = pdf.splitTextToSize(paragraph, CONTENT_WIDTH);
    pdf.text(lines, MARGIN, y, {
      lineHeightFactor: 1.6,
      maxWidth: CONTENT_WIDTH,
      align: 'justify',
    });
    y += lines.length * 6.2 + (index < paragraphs.length - 1 ? 8 : 20);
  });

  pdf.text('Sincerely,', MARGIN, y);
  if (stamp) pdf.addImage(stamp, 'PNG', PAGE_WIDTH - MARGIN - 40, y, 30, 30, undefined, 'FAST');
  y += 30;

  // Signature: the approver, with the day they approved it.
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.4);
  pdf.line(MARGIN, y, MARGIN + 45, y);
  y += 6;
  pdf.setFont(font, 'bold');
  pdf.text(letter.approverName, MARGIN, y);
  y += 5.5;
  pdf.setFont(font, 'normal');
  if (letter.approverDesignation) {
    pdf.text(letter.approverDesignation, MARGIN, y);
    y += 5;
  }
  pdf.text(letterhead.companyName, MARGIN, y);
  y += 5;
  pdf.text(`Approved on ${letter.approvedOn}`, MARGIN, y);
  y += 8;

  // The contact person, to verify the letter.
  pdf.setFontSize(10);
  pdf.text(`Contact: ${[letter.pocName, letter.pocDesignation].filter(Boolean).join(', ')}`, MARGIN, y);
  y += 5;
  pdf.setFont(font, 'bold');
  pdf.text('Email : ', MARGIN, y);
  pdf.setFont(font, 'normal');
  pdf.text(letter.pocEmail, MARGIN + 13, y);
  y += 5;
  pdf.setFont(font, 'bold');
  pdf.text('Mobile : ', MARGIN, y);
  pdf.setFont(font, 'normal');
  pdf.text(letter.pocMobile, MARGIN + 15, y);

  const footerY = PAGE_HEIGHT - 15;
  pdf.setFontSize(8);
  pdf.setTextColor(120, 120, 120);
  pdf.text(`© ${letterhead.companyName} ${orgWallClock(new Date()).year}`, PAGE_WIDTH / 2, footerY, {
    align: 'center',
  });
  if (branded) {
    pdf.text(
      'This document is digitally signed with SELISE Signature and should be treated as an original.',
      PAGE_WIDTH / 2,
      footerY + 4,
      { align: 'center' }
    );
  }

  pdf.save(`NOC_${letter.applicantName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
};
